<template>
  <!-- Source deleted (saved exploration no longer exists) -->
  <div v-if="config.unresolved" class="tile-state">
    <GoblinIcon variant="error" :size="28" />
    <p class="state-title">Source deleted</p>
    <p class="state-sub">The saved exploration behind this tile no longer exists.</p>
  </div>

  <!-- Single-value stat viz -->
  <div v-else-if="config.viz === 'stat'" class="stat-grid" :style="{ minHeight: height + 'px' }">
    <template v-if="statRows.length">
      <LgStatTile
        v-for="s in statRows"
        :key="s.name"
        :label="s.name"
        :value="s.value ?? '—'"
        :unit="meta.unit"
        :spark="s.spark"
      />
    </template>
    <div v-else class="tile-state">
      <template v-if="loading">
        <p class="state-sub">Loading…</p>
      </template>
      <template v-else>
        <GoblinIcon :variant="error ? 'error' : 'empty'" :size="28" />
        <p class="state-sub">{{ error || 'No data in this range.' }}</p>
      </template>
    </div>
  </div>

  <!-- Time-series viz -->
  <LgChart
    v-else
    :series="chartSeries"
    :type="chartTypeFor(config.viz)"
    :y-unit="meta.unit"
    :height="height"
    live
    :loading="loading && !chartSeries.length"
    :empty-label="error || 'No data in this range'"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { api, ApiError, type MetricSeries, type ResolvedMetricTileConfig } from '../../api.js';
import { metricTileQuery, metricTileMeta, chartTypeFor } from '../../lib/dashboard-layout.js';
import { cachedTileQuery } from '../../lib/tile-query-cache.js';
import { seriesName } from '../../lib/metric-exploration.js';
import { resolveVizPalette, colorForName } from '../../lib/viz-palette.js';
import type { ChartSeries } from '../../ui/LgChart.vue';
import LgChart from '../../ui/LgChart.vue';
import LgStatTile from '../../ui/LgStatTile.vue';
import GoblinIcon from '../GoblinIcon.vue';

const props = withDefaults(
  defineProps<{
    config: ResolvedMetricTileConfig;
    /** The dashboard clock's window (§3.4) — the tile never owns a timer. */
    from: number;
    to: number;
    height?: number;
  }>(),
  { height: 220 },
);

const emit = defineEmits<{ unauthorized: [] }>();

const series = ref<MetricSeries[]>([]);
const loading = ref(false);
const error = ref('');
let seq = 0;

const meta = computed(() => metricTileMeta(props.config));

const palette = ref<string[]>([]);
onMounted(() => {
  palette.value = resolveVizPalette(document.body);
});

const namedSeries = computed(() =>
  series.value.map((s) => ({
    name: seriesName(s.labels, meta.value.name, meta.value.splitBy),
    points: s.points,
  })),
);

const chartSeries = computed<ChartSeries[]>(() =>
  namedSeries.value.map((s) => ({
    name: s.name,
    points: s.points,
    color: colorForName(s.name, palette.value.length ? palette.value : ['#58a6ff']),
  })),
);

function lastValue(points: [number, number][]): number | null {
  for (let i = points.length - 1; i >= 0; i--) {
    if (Number.isFinite(points[i][1])) return points[i][1];
  }
  return null;
}

const statRows = computed(() =>
  namedSeries.value.map((s) => ({
    name: s.name,
    value: lastValue(s.points),
    spark: s.points.map((p) => p[1]),
  })),
);

async function fetchTile() {
  const body = metricTileQuery(props.config, props.from, props.to);
  if (!body) return; // unresolved — the template shows the deleted-source state
  const mySeq = ++seq;
  loading.value = true;
  try {
    // Identical tile queries on one dashboard coalesce into one request (§5).
    const res = await cachedTileQuery(`metric:${JSON.stringify(body)}`, () =>
      api.metrics.query(body),
    );
    if (mySeq !== seq) return;
    series.value = res.series;
    error.value = '';
  } catch (e) {
    if (mySeq !== seq) return;
    series.value = [];
    error.value = e instanceof ApiError ? e.message : 'Query failed.';
    if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
  } finally {
    if (mySeq === seq) loading.value = false;
  }
}

// The clock moving (live tick, range change, pause/resume) is the ONLY refresh
// trigger — scrape-aligned because the clock ticks on the scrape cadence.
watch(
  () => [props.from, props.to, props.config],
  () => fetchTile(),
  { immediate: true },
);
</script>

<style scoped>
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: var(--lg-space-2);
  align-content: start;
  padding: var(--lg-space-2);
  overflow-y: auto;
}
.tile-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-1);
  padding: var(--lg-space-4);
  min-height: 120px;
  color: var(--lg-text-faint);
  text-align: center;
  grid-column: 1 / -1;
}
.state-title {
  margin: 0;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  color: var(--lg-text-dim);
}
.state-sub {
  margin: 0;
  font-size: var(--lg-fs-xs);
  max-width: 32ch;
}
</style>
